<template>
  <div>
    <Title size="5" class="stk-mb-3">Invite Client to Portal</Title>
    
    <!-- Add notification area -->
    <Callout v-if="notification.message" :status="notification.status" class="stk-mb-4">
      {{ notification.message }}
    </Callout>
    
    <div class="stk-mb-4 stk-text-gray-700">
      Clients will be able to view document checklists, and upload requested documents.
    </div>
    
    <!-- When we have a link generated, show success callout and the link -->
    <div v-if="invitationLink || magicLinkData">
      <Callout 
        :status="invitationLink ? 'success' : 'info'" 
        :iconBefore="invitationLink ? 'fas fa-check' : 'fas fa-magic'" 
        class="stk-mb-4"
      >
        <span v-if="invitationLink">Invitation link generated for {{ entityEmail }}</span>
        <span v-else-if="magicLinkData">Magic link generated for {{ entityEmail }}</span>
      </Callout>
      
      <div class="stk-mb-4">
        <p class="stk-text-sm stk-text-gray-600" v-if="invitationLink">
          <strong>Standard Invitation:</strong> Client will create a password to access their portal.
        </p>
        <p class="stk-text-sm stk-text-gray-600" v-else-if="magicLinkData">
          <strong>Magic Link:</strong> One-click access that expires in 1 hour. No password required.
        </p>
        <p v-if="copied" class="stk-text-success stk-text-sm stk-mt-1">{{ copiedMessage }}</p>
      </div>
      
      <!-- Standard Invitation Link Actions -->
      <div v-if="invitationLink" class="stk-flex stk-justify-between">
        <Button 
          @click="copyInvitationLink" 
          type="primary"
          iconBefore="fas fa-copy"
        >
          Copy Invitation Link
        </Button>

        <Button 
          @click="resetForm" 
          type="secondary" 
          iconBefore="fas fa-plus"
        >
          Create New Invitation
        </Button>
      </div>

      <!-- Magic Link Actions -->
      <div v-else-if="magicLinkData" class="magic-link-actions">
        <div class="buttons">
          
          <!-- <Button 
            @click="copyMagicEmail" 
            type="secondary"
            iconBefore="fas fa-envelope"
            size="3"
            class="stk-w-full"
          >
            Copy Email Template
          </Button> -->

          <Button 
            @click="sendMagicLinkEmail" 
            iconBefore="fas fa-paper-plane"
            variant="primary"
            size="3"
            class="stk-w-full"
            :loading="sendingEmail"
          >
            Send Email Now
          </Button>
          <Button 
            @click="showEmailPreview = true" 
            size="3"
            iconBefore="fas fa-eye"
          >
            Preview Email
          </Button>
        <!-- Email Preview (collapsible) -->
        <div v-if="showEmailPreview" class="email-preview stk-mt-4">
          <div class="stk-bg-white stk-border stk-rounded-lg stk-shadow-sm stk-overflow-hidden">
            <!-- Email Preview Header -->
            <div class="stk-flex stk-justify-between stk-items-center stk-bg-gray-50 stk-px-4 stk-py-3 stk-border-b">
              <div class="stk-flex stk-items-center">
                <i class="fas fa-envelope stk-text-gray-500 stk-mr-2"></i>
                <span class="stk-font-medium stk-text-gray-700">Email Preview</span>
              </div>
              <Button 
                @click="showEmailPreview = false" 
                variant="ghost" 
                size="1" 
                iconOnly="fas fa-times"
              />
            </div>
            
            <!-- Email Content -->
            <div class="stk-p-4">
              <!-- Email Header -->
              <div class="stk-mb-4 stk-pb-3 stk-border-b stk-border-gray-200">
                <div class="stk-text-sm stk-text-gray-600 stk-mb-1">
                  <strong>To:</strong> {{ magicLinkData.client_email || entityEmail }}
                </div>
                <div class="stk-text-sm stk-text-gray-600 stk-mb-1">
                  <strong>Subject:</strong> {{ magicLinkData.email_subject }}
                </div>
                <div class="stk-text-xs stk-text-gray-500">
                  <strong>From:</strong> noreply@stake.com
                </div>
              </div>
              
              <!-- Email Body -->
              <div class="stk-text-sm stk-text-gray-800 stk-leading-relaxed stk-whitespace-pre-wrap stk-font-sans">
                {{ magicLinkData.email_body_text }}
              </div>
            </div>
          </div>
        </div>
        
        </div>

        

      </div>
    </div>
    
    <!-- Form to generate invitation link -->
    <form v-if="!invitationLink && !magicLinkData">
      <!-- <div class="stk-mb-4 field">
        <div class="stk-bg-blue-50 stk-border stk-border-blue-200 stk-rounded-lg stk-p-4">
          <div class="stk-flex stk-items-center stk-mb-2">
            <i class="fas fa-info-circle stk-text-blue-600 stk-mr-2"></i>
            <span class="stk-text-sm stk-font-medium stk-text-blue-800">Client Email</span>
          </div>
          <p class="stk-text-sm stk-text-blue-700">
            <strong>{{ entityEmail || 'No email set' }}</strong>
            <br>
            <span class="stk-text-xs">Using the email address associated with this client</span>
          </p>
        </div>
      </div> -->
      
      <!-- Enhanced invitation options with better visual separation -->
      <div class="invitation-options stk-mb-4">
        <div class="stk-grid stk-grid-cols-1 stk-gap-6">
          
          <!-- Standard Invitation Option -->
          <div class="invitation-option-card">
            <div class="stk-border stk-rounded-lg stk-p-6 stk-bg-white stk-shadow-sm">
              <div class="stk-flex stk-items-center stk-mb-4">
                <div class="stk-w-12 stk-h-12 stk-bg-blue-100 stk-rounded-full stk-flex stk-items-center stk-justify-center stk-mr-4">
                  <i class="fas fa-envelope stk-text-blue-600 stk-text-lg"></i>
                </div>
                <div>
                  <h3 class="stk-text-lg stk-font-semibold stk-text-gray-900">Standard Invitation</h3>
                  <p class="stk-text-sm stk-text-gray-600">Manual link sharing</p>
                </div>
              </div>
              
              <div class="stk-mb-4">
                <ul class="stk-text-sm stk-text-gray-700 stk-space-y-2">
                  <li class="stk-flex stk-items-start">
                    <i class="fas fa-check stk-text-green-500 stk-mr-2 stk-mt-0.5"></i>
                    <span>Client creates username and password</span>
                  </li>
                  <li class="stk-flex stk-items-start">
                    <i class="fas fa-check stk-text-green-500 stk-mr-2 stk-mt-0.5"></i>
                    <span>Permanent account access</span>
                  </li>
                  <li class="stk-flex stk-items-start">
                    <i class="fas fa-check stk-text-green-500 stk-mr-2 stk-mt-0.5"></i>
                    <span>You manually send the link</span>
                  </li>
                </ul>
              </div>
              
              <Button 
                @click="generateInvitationLink"
                type="primary" 
                :loading="loading"
                :disabled="loading || magicLinkLoading || !entityEmail"
                iconBefore="fas fa-link"
                size="3"
                class="stk-w-full"
              >
                Generate Invitation Link
              </Button>
            </div>
          </div>

          <!-- Magic Link Option -->
          <div class="magic-link-option-card">
            <div class="stk-border stk-rounded-lg stk-p-6 stk-bg-white stk-shadow-sm stk-relative">
              <!-- Magic sparkle badge -->
              <div class="stk-absolute stk-top-4 stk-right-4">
                <span class="stk-bg-purple-100 stk-text-purple-800 stk-text-xs stk-font-medium stk-px-2 stk-py-1 stk-rounded-full">
                  ✨ Magic
                </span>
              </div>
              
              <div class="stk-flex stk-items-center stk-mb-4">
                <div class="stk-w-12 stk-h-12 stk-bg-purple-100 stk-rounded-full stk-flex stk-items-center stk-justify-center stk-mr-4">
                  <i class="fas fa-magic stk-text-purple-600 stk-text-lg"></i>
                </div>
                <div>
                  <h3 class="stk-text-lg stk-font-semibold stk-text-gray-900">Magic Link</h3>
                  <p class="stk-text-sm stk-text-gray-600">One-click access</p>
                </div>
              </div>
              
              <div class="stk-mb-4">
                <ul class="stk-text-sm stk-text-gray-700 stk-space-y-2">
                  <li class="stk-flex stk-items-start">
                    <i class="fas fa-check stk-text-green-500 stk-mr-2 stk-mt-0.5"></i>
                    <span>No password required</span>
                  </li>
                  <li class="stk-flex stk-items-start">
                    <i class="fas fa-check stk-text-green-500 stk-mr-2 stk-mt-0.5"></i>
                    <span>Expires in 1 hour</span>
                  </li>
                  <li class="stk-flex stk-items-start">
                    <i class="fas fa-check stk-text-green-500 stk-mr-2 stk-mt-0.5"></i>
                    <span>We can send email automatically</span>
                  </li>
                </ul>
              </div>
              
              <Button 
                @click="generateMagicLink"
                type="secondary" 
                :loading="magicLinkLoading"
                :disabled="loading || magicLinkLoading || !entityEmail"
                iconBefore="fas fa-magic"
                size="3"
                class="stk-w-full magic-link-button"
              >
                Generate Magic Link
              </Button>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import {
  Button,
  Input,
  Label,
  Callout,
  Title,
  Level,
  LevelLeft,
  LevelRight,
} from '@stakeui';

export default {
  name: 'EntityInviteForm',
  components: {
    Button,
    Input,
    Label,
    Callout,
    Title,
    Level,
    LevelLeft,
    LevelRight,
  },
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
    advisorSlug: {
      type: String,
      required: true,
    },
    entityEmail: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      form: {
        email: this.entityEmail || '',
      },
      loading: false,
      magicLinkLoading: false,
      message: '',
      sendingEmail: false,
      messageStatus: 'info',
      errors: {},
      invitationLink: null,
      magicLinkData: null,
      copied: false,
      copiedMessage: '',
      showEmailPreview: false,
      notification: {
        message: '',
        status: 'info'
      },
    };
  },
  watch: {
    entityEmail(newVal) {
      if (newVal) {
        this.form.email = newVal;
      }
    },
  },
  methods: {
    validateForm() {
      this.errors = {};
      
      if (!this.entityEmail || !this.entityEmail.trim()) {
        this.errors.email = 'Email is required';
        return false;
      }
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.entityEmail)) {
        this.errors.email = 'Please enter a valid email address';
        return false;
      }
      
      return true;
    },
    resetForm() {
      this.invitationLink = null;
      this.magicLinkData = null;
      this.message = '';
      this.copied = false;
      this.copiedMessage = '';
      this.showEmailPreview = false;
      this.errors = {};
    },
    copyInvitationLink() {
      if (this.invitationLink) {
        navigator.clipboard.writeText(this.invitationLink).then(() => {
          this.copied = true;
          this.copiedMessage = 'Invitation link copied to clipboard!';
          this.notification = {
            message: 'Link copied to clipboard!',
            status: 'success'
          };
          setTimeout(() => {
            this.copied = false;
            this.copiedMessage = '';
            this.notification.message = '';
          }, 2000);
        });
      }
    },
    async generateInvitationLink() {
      if (!this.entityEmail) {
        this.message = 'No email address associated with this client';
        this.messageStatus = 'danger';
        return;
      }
      
      this.loading = true;
      this.message = '';
      
      try {
        const csrfToken = this.$root.$data.csrfToken;
        
        const requestData = {
          entity_external_id: this.entityExternalId,
          email: this.entityEmail,
          advisor_slug: this.advisorSlug
        };
        
        const response = await fetch('/api/v1/client-portal/invite/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify(requestData),
          credentials: 'same-origin',
        });
        
        const data = await response.json();
        
        if (response.ok) {
          // Store the invitation link
          this.invitationLink = data.invitation_url;
          
          this.message = `Invitation link generated for ${this.entityEmail}`;
          this.messageStatus = 'success';
          
          // Emit event with the email and information about the document portal destination
          this.$emit('invitation-link-generated', {
            email: this.entityEmail,
            link: data.invitation_url,
            redirectsToDocuments: true
          });
        } else {
          this.message = data.error || 'Failed to generate invitation link';
          this.messageStatus = 'danger';
        }
      } catch (error) {
        console.error('Error generating invitation link:', error);
        this.message = 'An error occurred while generating the invitation link';
        this.messageStatus = 'danger';
      } finally {
        this.loading = false;
      }
    },
    
    // Add a helper method to get destination information for the invitation link
    getInvitationDestination() {
      return "Client Portal Account Creation";
    },

    async generateMagicLink() {
      if (!this.entityEmail) {
        this.message = 'No email address associated with this client';
        this.messageStatus = 'danger';
        return;
      }
      
      this.magicLinkLoading = true;
      this.message = '';
      
      try {
        const csrfToken = this.$root.$data.csrfToken;
        
        const requestData = {
          entity_external_id: this.entityExternalId,
          email: this.entityEmail,
          advisor_slug: this.advisorSlug
        };
        
        const response = await fetch('/api/v1/client-portal/generate-magic-link/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify(requestData),
          credentials: 'same-origin',
        });
        
        const data = await response.json();
        
        if (response.ok) {
          // Store the magic link data
          this.magicLinkData = data;
          
          this.message = `Magic link generated for ${this.entityEmail}`;
          this.messageStatus = 'success';
          
          // Emit event with the magic link information
          this.$emit('invitation-link-generated', {
            email: this.entityEmail,
            link: data.magic_link,
            isMagicLink: true,
            expiresInMinutes: data.expires_in_minutes
          });
        } else {
          this.message = data.error || 'Failed to generate magic link';
          this.messageStatus = 'danger';
        }
      } catch (error) {
        console.error('Error generating magic link:', error);
        this.message = 'An error occurred while generating the magic link';
        this.messageStatus = 'danger';
      } finally {
        this.magicLinkLoading = false;
      }
    },

    copyMagicLink() {
      if (this.magicLinkData?.magic_link) {
        navigator.clipboard.writeText(this.magicLinkData.magic_link).then(() => {
          this.copied = true;
          this.copiedMessage = 'Magic link copied to clipboard!';
          setTimeout(() => {
            this.copied = false;
            this.copiedMessage = '';
          }, 2000);
        }).catch(err => {
          console.error('Failed to copy magic link:', err);
        });
      }
    },

    copyMagicEmail() {
      if (this.magicLinkData) {
        const emailContent = `Subject: ${this.magicLinkData.email_subject}\n\n${this.magicLinkData.email_body_text}`;
        navigator.clipboard.writeText(emailContent).then(() => {
          this.copied = true;
          this.copiedMessage = 'Email content copied to clipboard!';
          setTimeout(() => {
            this.copied = false;
            this.copiedMessage = '';
          }, 2000);
        }).catch(err => {
          console.error('Failed to copy email content:', err);
        });
      }
    },



    async sendMagicLinkEmail() {
      if (!this.magicLinkData) {
        return;
      }
      
      this.sendingEmail = true;
      
      try {
        const csrfToken = this.$root.$data.csrfToken;
        
        const requestData = {
          entity_external_id: this.entityExternalId,
          email: this.entityEmail,
          advisor_slug: this.advisorSlug,
        };
        
        const response = await fetch('/api/v1/client-portal/send-magic-link/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify(requestData),
          credentials: 'same-origin',
        });
        
        const data = await response.json();
        
        if (response.ok) {
          this.notification = {
            message: `Magic link email sent successfully to ${this.entityEmail}`,
            status: 'success'
          };
          setTimeout(() => {
            this.notification.message = '';
          }, 4000);
        } else {
          this.notification = {
            message: data.error || 'Failed to send magic link email',
            status: 'danger'
          };
          setTimeout(() => {
            this.notification.message = '';
          }, 4000);
        }
      } catch (error) {
        console.error('Error sending magic link email:', error);
        this.notification = {
          message: 'An error occurred while sending the email',
          status: 'danger'
        };
        setTimeout(() => {
          this.notification.message = '';
        }, 4000);
      } finally {
        this.sendingEmail = false;
      }
    }
  },
  mounted() {
    // Initialize with entity email if available
    if (this.entityEmail) {
      this.form.email = this.entityEmail;
    }
  },
};
</script> 
